import * as React from "react";
import { Link, NavLink, useHref, useLocation } from "react-router";
import { Dialog, Popover } from "radix-ui";
import { Menu as MenuIcon, X, ChevronDown, CircleUserRound, Ellipsis } from "lucide-react";
import { ErrorBoundary, TechnicalToggle, ThemeToggle, cn, useIsPhone, useScrollHint } from "@ozpanel/ui";

/**
 * AppShell — the one frame both customer consoles sit in: header, navigation, account menu,
 * optional bottom tabs, and an error boundary around the page.
 *
 * It replaces two hand-built headers that had drifted apart and were both broken at real widths
 * (measured 2026-11-02): at 320px the mailbox's menu button was pushed off-screen and the theme
 * switch sat on the title; at 700px its eight tabs overlapped; the account header was wider than
 * the screen on a phone and, with eleven tabs, pushed Sign out off a 1280px desktop. The rules it
 * keeps, at every width:
 *
 *   - The header never overflows. Tabs that do not fit go into "More", measured, not guessed.
 *   - A phone shows WHERE you are (the current page's name), the bell and one menu button.
 *   - Theme, technical details, the address and the way out live in ONE account menu — never
 *     scattered across the bar, never duplicated between the bar and the menu.
 *   - The bell is always in the bar: a notification you must open a menu to discover is one you
 *     will not discover.
 */

export interface ShellLink {
  label: string;
  to: string;
  end?: boolean;
}

export interface ShellNavItem extends ShellLink {
  key: string;
  /** Pages grouped under this item; shown as a tab strip at the top of the page. */
  children?: ShellLink[];
  /** Extra paths that belong to this item (e.g. /domains/x belongs to Websites). */
  also?: string[];
  /** Small icon for the bottom tab bar. */
  icon?: React.ReactNode;
}

export interface ShellAccount {
  email?: string;
  impersonating?: boolean;
  onSignOut: () => void;
  onStopImpersonating: () => void;
}

function matches(pathname: string, l: ShellLink): boolean {
  const to = l.to.replace(/\/+$/, "") || "/";
  if (l.end || to === "/") return pathname.replace(/\/+$/, "") === to.replace(/\/+$/, "") || (to === "/" && pathname === "/");
  return pathname === to || pathname.startsWith(to + "/");
}

// The ONE child a path belongs to: the most specific match. "/mail" also matches "/mail/import", and
// two tabs lit at once say nothing about where you are.
export function activeChild(links: ShellLink[] | undefined, pathname: string): ShellLink | undefined {
  let best: ShellLink | undefined;
  for (const l of links ?? []) {
    if (matches(pathname, l) && (!best || l.to.length > best.to.length)) best = l;
  }
  return best;
}

function itemActive(pathname: string, item: ShellNavItem): boolean {
  if (matches(pathname, item)) return true;
  if (item.children?.some((c) => matches(pathname, c))) return true;
  return (item.also ?? []).some((p) => pathname === p || pathname.startsWith(p + "/"));
}

/** The page's name, for the phone header: the active child's label, else the item's. */
export function useShellTitle(nav: ShellNavItem[]): string | undefined {
  const { pathname } = useLocation();
  const item = nav.find((n) => itemActive(pathname, n));
  if (!item) return undefined;
  const child = activeChild(item.children, pathname);
  return child?.label ?? item.label;
}

export function AppShell({
  brand,
  nav,
  bell,
  account,
  bottomTabs,
  homeHref,
  children,
}: {
  brand: React.ReactNode;
  nav: ShellNavItem[];
  bell?: React.ReactNode;
  account: ShellAccount;
  /** Keys of the nav items for a bottom tab bar on phones (the installed mailbox app). The menu
   *  takes the rest, behind a "More" tab. Omit for no bar. */
  bottomTabs?: string[];
  homeHref: string;
  children: React.ReactNode;
}) {
  const { pathname } = useLocation();
  const isPhone = useIsPhone();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const title = useShellTitle(nav);
  const homeAbs = useHref(homeHref);
  const active = nav.find((n) => itemActive(pathname, n));
  const tabs = isPhone && bottomTabs ? bottomTabs.map((k) => nav.find((n) => n.key === k)).filter((n): n is ShellNavItem => !!n) : null;

  // Moving to another page closes the menu — the click that chose a page has done its job.
  React.useEffect(() => setMenuOpen(false), [pathname]);

  return (
    <div className={cn("flex min-h-dvh flex-col bg-bg text-fg", tabs && "has-bottom-tabs")}>
      <header className="topbar-blur sticky top-0 z-30 border-b border-line pt-safe">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-2 px-3 sm:gap-4 sm:px-5">
          <Link to={homeHref} className="flex shrink-0 items-center gap-2" aria-label="Home">
            {brand}
          </Link>
          {isPhone ? (
            <div className="min-w-0 flex-1 truncate text-[15px] font-semibold" aria-live="polite">
              {title}
            </div>
          ) : (
            <OverflowNav nav={nav} pathname={pathname} />
          )}
          <div className="flex shrink-0 items-center gap-1">
            {bell}
            {isPhone ? (
              !tabs && (
                <button
                  type="button"
                  aria-label={menuOpen ? "Close menu" : "Menu"}
                  aria-expanded={menuOpen}
                  onClick={() => setMenuOpen((v) => !v)}
                  className="grid size-11 place-items-center rounded-lg text-muted hover:bg-surface2 hover:text-fg"
                >
                  {menuOpen ? <X size={20} /> : <MenuIcon size={20} />}
                </button>
              )
            ) : (
              <AccountPopover account={account} />
            )}
          </div>
        </div>
      </header>

      {active?.children && active.children.length > 1 && <SubNav links={active.children} pathname={pathname} />}

      <main className={cn("mx-auto w-full max-w-6xl flex-1 px-3 py-4 sm:px-5 sm:py-6", tabs && "pb-[calc(76px+env(safe-area-inset-bottom))]")}>
        <ErrorBoundary homeHref={homeAbs} resetKey={pathname}>
          {children}
        </ErrorBoundary>
      </main>

      {tabs && <BottomTabs tabs={tabs} pathname={pathname} onMore={() => setMenuOpen(true)} moreActive={!!active && !tabs.includes(active)} />}

      <MenuSheet open={menuOpen} onOpenChange={setMenuOpen} nav={nav} pathname={pathname} account={account} />
    </div>
  );
}

/** The desk's tab row. Whatever does not fit in the space left goes into "More" — measured on
 *  every resize, because the right answer depends on the labels and the font, not on a guess. */
function OverflowNav({ nav, pathname }: { nav: ShellNavItem[]; pathname: string }) {
  const wrap = React.useRef<HTMLDivElement>(null);
  const measure = React.useRef<HTMLDivElement>(null);
  const [fit, setFit] = React.useState(nav.length);

  React.useLayoutEffect(() => {
    const el = wrap.current;
    const m = measure.current;
    if (!el || !m) return;
    const compute = () => {
      const widths = Array.from(m.children).map((c) => (c as HTMLElement).offsetWidth + 4);
      const more = 84;
      const avail = el.clientWidth;
      let used = 0;
      let n = 0;
      for (let i = 0; i < widths.length; i++) {
        const w = widths[i] ?? 0;
        const need = used + w + (i < widths.length - 1 ? more : 0);
        if (need > avail) break;
        used += w;
        n++;
      }
      setFit(n);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [nav]);

  const shown = nav.slice(0, fit);
  const rest = nav.slice(fit);
  const linkClass = (on: boolean) =>
    cn(
      "inline-flex h-9 shrink-0 items-center whitespace-nowrap rounded-lg px-3 text-[13.5px]",
      on ? "bg-accentsoft font-medium text-accent" : "text-muted hover:bg-surface2 hover:text-fg",
    );
  return (
    <div ref={wrap} className="relative flex min-w-0 flex-1 items-center">
      <div ref={measure} aria-hidden className="pointer-events-none invisible absolute flex gap-1 whitespace-nowrap">
        {nav.map((n) => (
          <span key={n.key} className="inline-flex h-9 items-center px-3 text-[13.5px] font-medium">
            {n.label}
          </span>
        ))}
      </div>
      <nav aria-label="Main" className="flex min-w-0 items-center gap-1">
        {shown.map((n) => (
          <NavLink key={n.key} to={n.to} end={n.end} className={() => linkClass(itemActive(pathname, n))}>
            {n.label}
          </NavLink>
        ))}
        {rest.length > 0 && (
          <Popover.Root>
            <Popover.Trigger
              className={cn(linkClass(rest.some((n) => itemActive(pathname, n))), "gap-1")}
              aria-label="More pages"
            >
              More <ChevronDown size={14} aria-hidden />
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content align="start" sideOffset={6} collisionPadding={12} className="z-50 min-w-48 rounded-xl border border-line bg-surface p-1.5 shadow-lg">
                {rest.map((n) => (
                  <Popover.Close asChild key={n.key}>
                    <NavLink
                      to={n.to}
                      end={n.end}
                      className={() =>
                        cn(
                          "block rounded-lg px-3 py-2 text-[13.5px]",
                          itemActive(pathname, n) ? "bg-accentsoft text-accent" : "text-fg hover:bg-surface2",
                        )
                      }
                    >
                      {n.label}
                    </NavLink>
                  </Popover.Close>
                ))}
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        )}
      </nav>
    </div>
  );
}

/** The pages inside a group (Apps · Databases · Redis), as tabs at the top of the page. */
function SubNav({ links, pathname }: { links: ShellLink[]; pathname: string }) {
  const hint = useScrollHint<HTMLElement>('[aria-current="page"]');
  const current = activeChild(links, pathname);
  return (
    <div className="border-b border-line bg-surface">
      <nav ref={hint.ref} style={hint.style} aria-label="Section" className="no-scrollbar mx-auto flex max-w-6xl gap-1 overflow-x-auto px-3 sm:px-5">
        {links.map((l) => {
          const on = l === current;
          return (
            <Link
              key={l.to}
              to={l.to}
              aria-current={on ? "page" : undefined}
              className={cn(
                "inline-flex h-11 shrink-0 items-center border-b-2 px-3 text-[13.5px] whitespace-nowrap",
                on ? "border-[var(--ozp-accent)] font-medium text-fg" : "border-transparent text-muted hover:text-fg",
              )}
            >
              {l.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

/** The installed app's thumb bar: the four places used most, and More for the rest. */
function BottomTabs({
  tabs,
  pathname,
  onMore,
  moreActive,
}: {
  tabs: ShellNavItem[];
  pathname: string;
  onMore: () => void;
  moreActive: boolean;
}) {
  const cell = (on: boolean) =>
    cn("flex h-14 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-[11px]", on ? "text-accent" : "text-muted");
  return (
    <nav aria-label="Main" className="topbar-blur fixed inset-x-0 bottom-0 z-30 flex border-t border-line pb-safe">
      {tabs.map((t) => (
        <NavLink key={t.key} to={t.to} end={t.end} className={() => cell(itemActive(pathname, t))}>
          <span aria-hidden className="grid h-6 place-items-center">{t.icon}</span>
          <span className="truncate">{t.label}</span>
        </NavLink>
      ))}
      <button type="button" onClick={onMore} className={cell(moreActive)}>
        <span aria-hidden className="grid h-6 place-items-center">
          <Ellipsis size={20} />
        </span>
        More
      </button>
    </nav>
  );
}

function AccountSection({ account, onDone }: { account: ShellAccount; onDone?: () => void }) {
  return (
    <div className="space-y-3">
      {account.email && <div className="truncate text-[12.5px] text-muted">{account.email}</div>}
      <div className="flex items-center justify-between gap-3 text-[13px]">
        <span>Theme</span>
        <ThemeToggle />
      </div>
      <TechnicalToggle />
      <button
        type="button"
        onClick={() => {
          onDone?.();
          if (account.impersonating) account.onStopImpersonating();
          else account.onSignOut();
        }}
        className="flex min-h-11 w-full items-center justify-center rounded-[9px] border border-line2 bg-surface text-[13.5px] font-medium text-fg hover:border-[var(--ozp-accent-line)]"
      >
        {account.impersonating ? "Back to your account" : "Sign out"}
      </button>
    </div>
  );
}

function AccountPopover({ account }: { account: ShellAccount }) {
  return (
    <Popover.Root>
      <Popover.Trigger
        aria-label="Account"
        className="inline-flex h-10 max-w-56 items-center gap-2 rounded-lg px-2 text-[13px] text-muted hover:bg-surface2 hover:text-fg"
      >
        <CircleUserRound size={20} aria-hidden />
        <span className="hidden truncate lg:inline">{account.email}</span>
        <ChevronDown size={14} aria-hidden />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} collisionPadding={12} className="z-50 w-[min(300px,calc(100vw-24px))] rounded-xl border border-line bg-surface p-4 shadow-lg">
          <AccountSection account={account} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** The phone menu: every page, then the account. A sheet over the page — it does not push the page
 *  down, and it closes on a tap outside, Escape, or choosing a page. */
function MenuSheet({
  open,
  onOpenChange,
  nav,
  pathname,
  account,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  nav: ShellNavItem[];
  pathname: string;
  account: ShellAccount;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 flex max-h-[88dvh] flex-col rounded-t-2xl border border-line bg-surface pb-safe outline-none">
          <div className="flex items-center justify-between border-b border-line px-4 py-2">
            <Dialog.Title className="text-[15px] font-semibold">Menu</Dialog.Title>
            <Dialog.Close aria-label="Close menu" className="grid size-11 place-items-center rounded-lg text-muted hover:bg-surface2">
              <X size={20} />
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">Pages and account</Dialog.Description>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-2">
            <nav aria-label="Pages" className="space-y-0.5">
              {nav.map((n) => (
                <div key={n.key}>
                  <NavLink
                    to={n.to}
                    end={n.end}
                    onClick={() => onOpenChange(false)}
                    className={() =>
                      cn(
                        "flex min-h-11 items-center gap-3 rounded-lg px-3 text-[15px]",
                        itemActive(pathname, n) && !(n.children && n.children.length > 1) ? "bg-accentsoft font-medium text-accent" : "text-fg hover:bg-surface2",
                      )
                    }
                  >
                    {n.icon && <span aria-hidden className="text-muted">{n.icon}</span>}
                    {n.label}
                  </NavLink>
                  {n.children && n.children.length > 1 && (
                    <div className="mb-1 ml-9 space-y-0.5">
                      {n.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          end={c.end}
                          onClick={() => onOpenChange(false)}
                          className={() =>
                            cn(
                              "flex min-h-10 items-center rounded-lg px-3 text-[14px]",
                              c === activeChild(n.children, pathname) ? "bg-accentsoft font-medium text-accent" : "text-muted hover:bg-surface2 hover:text-fg",
                            )
                          }
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
            <div className="mt-3 border-t border-line px-1 pt-3 pb-2">
              <AccountSection account={account} onDone={() => onOpenChange(false)} />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
